import { useTranslations } from '@aikoa/i18n/react'
import { BlurView } from 'expo-blur'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react'
import { Platform, StyleSheet, View } from 'react-native'
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import Auth from '../Auth'
import { LanguageSelector } from '../LanguageSelector'
import ContentBox from '../layout/ContentBox'
import { SectionContent } from '../layout/Sections'
import { Body } from '../typography/Content'
import { Heading1, Heading2 } from '../typography/Titles'
import { LinkButton } from '../ui/button'
import { useSession } from '../user/SessionProvider'

const AnimatedBlurView = Animated.createAnimatedComponent(BlurView)

type PreviewGateState = 'locked' | 'awaiting-data' | 'animating-out' | 'unlocked'

type PublicPreviewContextType = {
  isInPublicPreview: boolean
  openAuthModal: () => void
  notifyLeadDataReady: () => void
}

const defaultContextValue: PublicPreviewContextType = {
  isInPublicPreview: false,
  openAuthModal: () => {},
  notifyLeadDataReady: () => {},
}

const PublicPreviewContext = createContext<PublicPreviewContextType>(defaultContextValue)

export function PublicPreviewProvider({ children }: PropsWithChildren) {
  const { session, isAnonymous } = useSession()
  const isAuthenticated = !isAnonymous && !!session?.user

  const [gateState, setGateState] = useState<PreviewGateState>(
    isAuthenticated ? 'unlocked' : 'locked'
  )

  const blurIntensity = useSharedValue<number | undefined>(isAuthenticated ? 0 : 80)
  const overlayOpacity = useSharedValue(isAuthenticated ? 0 : 1)
  const gateStateRef = useRef(gateState)
  gateStateRef.current = gateState
  const prevIsAuthenticated = useRef(isAuthenticated)

  useEffect(() => {
    const wasAuthenticated = prevIsAuthenticated.current
    prevIsAuthenticated.current = isAuthenticated

    if (!isAuthenticated && wasAuthenticated) {
      blurIntensity.value = 80
      overlayOpacity.value = withTiming(1, { duration: 200 })
      setGateState('locked')
    }

    if (isAuthenticated && !wasAuthenticated) {
      setGateState((current) => (current === 'locked' ? 'awaiting-data' : current))
    }
  }, [isAuthenticated, blurIntensity, overlayOpacity])

  const openAuthModal = useCallback(() => {
    blurIntensity.value = 80
    overlayOpacity.value = withTiming(1, { duration: 200 }, () => {
      runOnJS(setGateState)('locked')
    })
  }, [blurIntensity, overlayOpacity])

  const notifyLeadDataReady = useCallback(() => {
    if (gateStateRef.current !== 'awaiting-data') {
      return
    }

    setGateState('animating-out')

    blurIntensity.value = withTiming(0, { duration: 600 })
    overlayOpacity.value = withTiming(0, { duration: 500 }, (finished) => {
      if (finished) {
        runOnJS(setGateState)('unlocked')
      }
    })
  }, [blurIntensity, overlayOpacity])

  const overlayAnimatedStyle = useAnimatedStyle(() => ({
    opacity: overlayOpacity.value,
  }))

  const isOverlayVisible = gateState !== 'unlocked'

  return (
    <PublicPreviewContext.Provider
      value={{
        isInPublicPreview: !isAuthenticated,
        openAuthModal,
        notifyLeadDataReady,
      }}>
      {children}
      {isOverlayVisible && (
        <Animated.View style={[StyleSheet.absoluteFill, overlayAnimatedStyle]}>
          {Platform.OS === 'web' ? (
            <View style={StyleSheet.absoluteFill} className="bg-tertiary/30 backdrop-blur-md" />
          ) : (
            <AnimatedBlurView
              intensity={blurIntensity}
              tint="systemMaterial"
              style={StyleSheet.absoluteFill}
            />
          )}
          {gateState === 'locked' && (
            <View className="flex-1 items-center justify-center p-6">
              <PublicPreviewAuthModal />
            </View>
          )}
        </Animated.View>
      )}
    </PublicPreviewContext.Provider>
  )
}

export function usePublicPreviewAuth() {
  const context = useContext(PublicPreviewContext)

  if (!context) {
    return defaultContextValue
  }

  return context
}

function PublicPreviewAuthModal() {
  const { t } = useTranslations('common')

  return (
    <ContentBox
      className="flex-none justify-center shadow-sm"
      contentContainerClassName="gap-8"
      variant="default">
      <LanguageSelector className="absolute right-4 top-3" iconClassName="text-foreground" />
      <Auth
        onAuthenticated={() => {}}
        inputVariant="box"
        className="mx-auto"
        content={
          <View className="w-full gap-2">
            <Heading1 className="text-center text-2xl">
              {t(($) => $.publicPreview.loginTitle)}
            </Heading1>
            <SectionContent>
              <Body className="text-center">{t(($) => $.publicPreview.loginDescription)}</Body>
            </SectionContent>
          </View>
        }
      />
      <View className="gap-3 border-t border-border pt-6">
        <Heading2 className="text-center">{t(($) => $.login.newUserTitle)}</Heading2>
        <Body className="mb-2 text-pretty text-center text-lg/normal">
          <Body>
            {t(($) => $.login.pilotDescription.before)}
            <Body className="font-semibold">{t(($) => $.login.pilotDescription.pilot)}</Body>
            {t(($) => $.login.pilotDescription.after)}
          </Body>
        </Body>
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <LinkButton variant="primary" size="lg" href="mailto:info@aikoa.fi">
          info@aikoa.fi
        </LinkButton>
      </View>
    </ContentBox>
  )
}
